import { useRef, useMemo, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import * as THREE from 'three';

const BAR_GAP = 0.15;
const BAR_SIZE = 0.8;

function Bar({ position, height, color, opacity }) {
  const ref = useRef();
  const [hovered, setHovered] = useState(false);

  return (
    <mesh
      ref={ref}
      position={[position[0], height / 2, position[2]]}
      onPointerOver={(e) => { e.stopPropagation(); setHovered(true); }}
      onPointerOut={() => setHovered(false)}
    >
      <boxGeometry args={[BAR_SIZE, height, BAR_SIZE]} />
      <meshStandardMaterial
        color={hovered ? '#ffffff' : color}
        opacity={hovered ? 1 : opacity}
        transparent
      />
    </mesh>
  );
}

function GridFloor({ rows, cols, spacing }) {
  const width = cols * spacing;
  const depth = rows * spacing;
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[width / 2 - spacing / 2, -0.01, depth / 2 - spacing / 2]}>
      <planeGeometry args={[width + 0.4, depth + 0.4]} />
      <meshStandardMaterial color="#1a1a2e" opacity={0.3} transparent />
    </mesh>
  );
}

function AxisLabels({ labels, axis, spacing, rows, cols }) {
  if (!labels) return null;
  return labels.map((label, i) => {
    const pos = axis === 'x'
      ? [i * spacing, -0.3, cols * spacing - spacing / 2 + 0.6]
      : [-0.9, -0.3, i * spacing];
    return (
      <Text
        key={i}
        position={pos}
        fontSize={0.35}
        color="#adb5bd"
        anchorX="center"
        anchorY="middle"
        rotation={axis === 'z' ? [0, Math.PI / 4, 0] : [0, 0, 0]}
      >
        {String(label)}
      </Text>
    );
  });
}

function Scene({ matrix, maxHeight, rowLabels, colLabels, colorFn }) {
  const spacing = BAR_SIZE + BAR_GAP;
  const rows = matrix.length;
  const cols = matrix[0]?.length || 0;

  const bars = useMemo(() => {
    const result = [];
    const maxVal = Math.max(...matrix.flat());
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const val = matrix[r][c];
        if (val <= 0) continue;
        const height = (val / maxVal) * maxHeight;
        const { color, opacity } = colorFn(val, maxVal, r, c);
        result.push({
          key: `${r}-${c}`,
          position: [c * spacing, 0, r * spacing],
          height: Math.max(height, 0.05),
          color,
          opacity,
        });
      }
    }
    return result;
  }, [matrix, maxHeight, rows, cols, colorFn]);

  const center = useMemo(() => [
    ((cols - 1) * spacing) / 2,
    maxHeight / 3,
    ((rows - 1) * spacing) / 2,
  ], [rows, cols, spacing, maxHeight]);

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 10, 5]} intensity={0.8} />
      <pointLight position={[-5, 8, -5]} intensity={0.3} />
      <group position={[-center[0], -center[1], -center[2]]}>
        <GridFloor rows={rows} cols={cols} spacing={spacing} />
        {bars.map((bar) => (
          <Bar key={bar.key} {...bar} />
        ))}
        <AxisLabels labels={colLabels} axis="x" spacing={spacing} rows={rows} cols={cols} />
        <AxisLabels labels={rowLabels} axis="z" spacing={spacing} rows={rows} cols={cols} />
      </group>
      <OrbitControls
        enablePan={false}
        enableZoom={true}
        minDistance={3}
        maxDistance={20}
        minPolarAngle={0.2}
        maxPolarAngle={Math.PI / 2.1}
      />
    </>
  );
}

export default function Matrix3D({ matrix, rowLabels, colLabels, maxHeight = 5, colorFn, height = 280 }) {
  if (!matrix || matrix.length === 0) return null;

  return (
    <div style={{ width: '100%', height, cursor: 'grab' }}>
      <Canvas
        camera={{ position: [8, 6, 8], fov: 45 }}
        style={{ background: 'transparent' }}
      >
        <Scene
          matrix={matrix}
          maxHeight={maxHeight}
          rowLabels={rowLabels}
          colLabels={colLabels}
          colorFn={colorFn}
        />
      </Canvas>
    </div>
  );
}
